import * as React from 'react';
import BuildingsList from '../Shared/BuildingsList';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, LinearProgress, Grid, IconButton, Typography, Grow, Stack } from '@mui/material';
import { BottomIconButton } from '../Shared/BottomIconButton';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import MobileCamera from '../Shared/MobileCamera';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { useEmployeeContext } from '../../../Contexts/Providers/EmployeeLayoutProvider';
import StavarioLogoBackground from '../Shared/StavarioLogoBackground';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useMobileApi } from '../../../Api/Mobile/useMobileApi';
import { AddImagesToBuildingRequest, ImageCategoryMobileDTO, InsertLogRecordRequest, MobileBuildingsVovClient, MobileRecordsVovClient } from '../../../ApiOld/Api/ApiServerVov';
import { AddAPhotoOutlined, CameraAlt, Folder, FolderOutlined } from '@mui/icons-material';
import { loadQueueFromIDB, saveQueueToIDB } from '../Shared/IDbUtils';
import SyncIcon from '@mui/icons-material/Sync';
import truncateText from '../../Shared/String/TruncateString';

export interface MobilePhotoDTO {
    image: string;
    taken: Date | null;
    categoryId?: number;
}

export default function MobilePhotosScreen() {
    const { i18n } = useTranslation();
    const { setModuleName, setModuleButton, networkType, diagnosticAlerts, deviceId, deviceModel, versionWithBuild } = useEmployeeContext();
    const { authorizedMobileEmployeeApi } = useMobileApi(MobileBuildingsVovClient);
    const { authorizedMobileEmployeeApi: recordsApi } = useMobileApi(MobileRecordsVovClient);
    const { t } = useTranslation();
    const [buildingId, setBuildingId] = useState<number | undefined>(undefined);
    const [cameraOpen, setCameraOpen] = useState<boolean>(false);
    const [imageQueue, setImageQueue] = useState<MobilePhotoDTO[]>([]);
    const [groupedImages, setGroupedImages] = useState<Record<string, MobilePhotoDTO[]>>({});
    const [startUpload, setStartUpload] = useState<boolean>(false);
    const imageQueueRef = useRef<MobilePhotoDTO[]>([]);
    const [forceUpload, setForceUpload] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [categoryId, setCategoryId] = useState<number>();
    const [categories, setCategories] = useState<ImageCategoryMobileDTO[]>([]);

    useEffect(() => {
        if (buildingId) {
            (async () => {
                const savedQueue = await loadQueueFromIDB(buildingId);
                setImageQueue(savedQueue);
            })();
        } else {
            setImageQueue([]);
        }
    }, [buildingId]);

    // useEffect(() => {
    //     if (buildingId) {
    //         saveQueueToIDB(imageQueue, buildingId);
    //     }
    // }, [imageQueue, buildingId]);

    const groupImagesByDate = useCallback((images: MobilePhotoDTO[]) => {
        const grouped: Record<string, MobilePhotoDTO[]> = {};
        images.forEach((image) => {
            const date = image.taken?.toLocaleDateString(i18n.language) ?? '';
            if (date) {
                if (!grouped[date]) {
                    grouped[date] = [];
                }
                grouped[date].push(image);
            }
        });
        return grouped;
    }, [i18n.language]);

    useEffect(() => {
        imageQueueRef.current = imageQueue;
        setGroupedImages(groupImagesByDate(imageQueue));
    }, [groupImagesByDate, imageQueue]);

    const handleUpload = useCallback(async () => {
        if (!imageQueueRef.current.length || !buildingId) {
            recordsApi.insertLog(new InsertLogRecordRequest({
                action: '(MobilePhotosScreen) Nothing to upload or buildingId is undefined.',
                deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
            }));
            return;
        }
        const totalImages = imageQueueRef.current.length;
        recordsApi.insertLog(new InsertLogRecordRequest({
            action: `(MobilePhotosScreen) Total images to upload: ${totalImages}`,
            deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
        }));

        const uploadNext = async () => {
            if (imageQueueRef.current.length === 0) {
                recordsApi.insertLog(new InsertLogRecordRequest({
                    action: '(MobilePhotosScreen) No more images to upload.',
                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                }));
                setStartUpload(false);
                setIsUploading(false);
                setUploadProgress(0);
                return;
            }

            const [currentImage, ...remainingQueue] = imageQueueRef.current;

            if (!currentImage?.image) {
                recordsApi.insertLog(new InsertLogRecordRequest({
                    action: '(MobilePhotosScreen) Skipping image with no data.',
                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                }));
                setTimeout(async () => {
                    imageQueueRef.current = remainingQueue;
                    setImageQueue(remainingQueue);
                    await uploadNext();
                }, 2000);
                return;
            }

            try {
                const effectiveCategory = currentImage.categoryId ?? categoryId;
                const payload = new AddImagesToBuildingRequest({
                    buildingId,
                    images: [currentImage.image.split('base64,')[1]],
                    categoryId: effectiveCategory
                });
                recordsApi.insertLog(new InsertLogRecordRequest({
                    action: '(MobilePhotosScreen) About to call addImagesToBuilding endpoint: ' + JSON.stringify(payload),
                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                }));
                const res = await authorizedMobileEmployeeApi.addImagesToBuilding(payload);

                if (res.ok) {
                    recordsApi.insertLog(new InsertLogRecordRequest({
                        action: '(MobilePhotosScreen) Image uploaded successfully.',
                        deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                    }));
                    imageQueueRef.current = remainingQueue;
                    setImageQueue(remainingQueue);
                    await saveQueueToIDB(remainingQueue, buildingId);
                    setUploadProgress(((totalImages - remainingQueue.length) / totalImages) * 100);
                    await uploadNext();
                } else {
                    recordsApi.insertLog(new InsertLogRecordRequest({
                        action: '(MobilePhotosScreen) Failed to upload image',
                        deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                    }));
                    setStartUpload(false);
                    setIsUploading(false);
                }
            } catch (error) {
                recordsApi.insertLog(new InsertLogRecordRequest({
                    action: '(MobilePhotosScreen) Error while uploading image' + JSON.stringify(error),
                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                }));
                console.error("[uploadNext] Error while uploading image:", error);
                setStartUpload(false);
                setIsUploading(false);
            }
        };

        await uploadNext();
    }, [buildingId, recordsApi, deviceModel, deviceId, versionWithBuild, categoryId, authorizedMobileEmployeeApi]);

    useEffect(() => {
        const uploadImages = async () => {
            if (startUpload && !isUploading) {
                recordsApi.insertLog(new InsertLogRecordRequest({
                    action: '(MobilePhotosScreen) about to start uploading from useEffect',
                    deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                }));
                setIsUploading(true);
                await handleUpload();
            }
        };

        if (networkType !== 'cellular' || forceUpload) {
            uploadImages();
        }
    }, [startUpload, forceUpload, networkType, isUploading, handleUpload, recordsApi, deviceModel, deviceId, versionWithBuild]);

    useEffect(() => {
        if (buildingId) {
            authorizedMobileEmployeeApi.getImageCategories(buildingId).then((res) => {
                setCategories(res.categories ?? []);
            });
        }
        else setCategories([]);
    }, [buildingId]);

    if (!buildingId) {
        return (
            <Box>
                <BuildingsList onBuildingClick={(e) => {
                    setBuildingId(e?.buildingId);
                    setModuleName(e?.name ?? 'Photos');
                    setModuleButton && setModuleButton(
                        <IconButton onClick={() => {
                            setBuildingId(undefined);
                            setModuleName('Photos');
                            setModuleButton && setModuleButton(null);
                        }}>
                            <ArrowBackIosNewIcon />
                        </IconButton>
                    );
                }} />
            </Box>
        );
    } else if (cameraOpen) {
        return (
            <MobileCamera
                allowMultiple
                allowUpload
                defaultCameraType='environment'
                onConfirm={(photos: MobilePhotoDTO[]) => {
                    recordsApi.insertLog(new InsertLogRecordRequest({
                        action: '(MobilePhotosScreen) mobileCamera confirm - with ' + photos.length + ' photos ' + JSON.stringify(photos),
                        deviceInfo: deviceModel + ' ' + deviceId + ' ' + versionWithBuild,
                    }));

                    setImageQueue(prevQueue => {
                        const existingImages = new Set(prevQueue.map(item => item.image));
                        const newPhotos = Array.isArray(photos)
                            ? photos.filter(photo => !existingImages.has(photo.image)).map(photo => ({ ...photo, categoryId }))
                            : ([photos] as MobilePhotoDTO[]).filter(photo => !existingImages.has(photo.image)).map(photo => ({ ...photo, categoryId }));
                        const updatedQueue = [...prevQueue, ...newPhotos];
                        if (buildingId) {
                            saveQueueToIDB(updatedQueue, buildingId);
                        }
                        return updatedQueue;
                    });
                    setCameraOpen(false);
                    setStartUpload(true);
                }}
                onBackButtonClicked={() => setCameraOpen(false)}
            />
        );
    }

    return (
        <Box sx={{ position: 'relative', width: '100vw', height: 'calc(100vh - 48px)', overflow: 'hidden' }}>
            <Box sx={{
                position: 'relative',
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                paddingBottom: '94px'
            }}>
                <Grow in={imageQueue.length > 0}>
                    <Box>
                        {imageQueue.length > 0 && (
                            <Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', background: 'white', m: 2, borderRadius: 4, p: 2, textAlign: 'center', boxShadow: 3 }}>
                                    {isUploading ? (
                                        <Box sx={{ width: '100%' }}>
                                            <LinearProgress
                                                sx={{
                                                    my: 1,
                                                    mx: 2,
                                                    borderRadius: 8,
                                                    height: 8,
                                                }}
                                                variant="buffer"
                                                value={uploadProgress}
                                                valueBuffer={100}
                                            />
                                            <Typography sx={{ fontSize: 16, fontWeight: '500', color: 'black', mx: 2, textAlign: 'center' }}>
                                                {t('UploadInProgress...')}
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Box>
                                            {networkType === 'cellular' && !forceUpload ? (

                                                <Stack direction='column' spacing={2} alignItems="center" sx={{ mt: 2 }}>
                                                    <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: 'white' }}>
                                                        {imageQueue.length} {t('photosHaventBeenSynchronized.')}
                                                    </Typography>
                                                    <Typography
                                                        onClick={() => {
                                                            setForceUpload(true);
                                                            setStartUpload(true);
                                                        }}
                                                        sx={{
                                                            fontSize: 16,
                                                            fontWeight: 'bold',
                                                            color: '#FCB501',
                                                            textDecoration: 'underline',
                                                            cursor: 'pointer'
                                                        }}
                                                    >
                                                        {t('SendViaMobileData')}
                                                    </Typography>
                                                </Stack>
                                            ) : (
                                                <Stack direction='row' spacing={1} justifyContent={'space-between'} sx={{ mt: 2 }}>
                                                    <Typography sx={{ fontSize: 16, fontWeight: 'bold', color: 'white' }}>
                                                        {imageQueue.length} {t('photosHaventBeenSynchronized.')}
                                                    </Typography>
                                                    <SyncIcon
                                                        sx={{ background: '#FFC600', borderRadius: 50, cursor: 'pointer', width: 32, height: 32, }}
                                                        onClick={() => {
                                                            setStartUpload(true);
                                                        }}
                                                    />
                                                </Stack>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', background: 'white', mx: 2, borderRadius: 4, mt: 2, boxShadow: 3 }}>
                                    {Object.entries(groupedImages).map(([date, images], index) => (
                                        <Box key={'key_' + index} sx={{ mb: 4 }}>
                                            <Typography sx={{ mx: 2, mt: 2, fontWeight: 'bold' }}>
                                                {date}
                                            </Typography>
                                            {/* <Grid container spacing={2} sx={{ px: 2 }}>
                                                {images.map((image, imgIndex) => (
                                                    <Grid item xs={4} key={'img' + imgIndex}>
                                                        <img src={image.image} alt={image.image} style={{ width: '100%' }} />
                                                    </Grid>
                                                ))}
                                            </Grid> */}
                                            <Grid container spacing={2} sx={{ px: 2, flexWrap: 'nowrap' }}>
                                                {/* First image */}
                                                {images.length > 0 && (
                                                    <Grid item xs={6} key={'img0'}>
                                                        <img src={images[0].image} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
                                                    </Grid>
                                                )}

                                                {/* Second image */}
                                                {images.length > 1 && (
                                                    <Grid item xs={images.length === 2 ? 6 : 4} key={'img1'}>
                                                        <img src={images[1].image} alt="thumbnail" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }} />
                                                    </Grid>
                                                )}
                                                {images.length > 2 && (
                                                    <Grid item xs={2} key={'more-images'}>
                                                        <Box sx={{
                                                            position: 'relative',
                                                            height: '100%',
                                                            width: '100%',
                                                        }}>
                                                            <img
                                                                src={images[2].image}
                                                                alt="more"
                                                                style={{
                                                                    width: '100%',
                                                                    height: '100%',
                                                                    objectFit: 'cover',
                                                                    borderRadius: '4px',
                                                                    filter: 'brightness(0.7)'
                                                                }}
                                                            />
                                                            <Box sx={{
                                                                position: 'absolute',
                                                                top: 0,
                                                                left: 0,
                                                                right: 0,
                                                                bottom: 0,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                backgroundColor: 'rgba(0,0,0,0.5)',
                                                                borderRadius: '4px',
                                                            }}>
                                                                <Typography sx={{ color: 'white', fontWeight: 'bold', fontSize: '1rem' }}>
                                                                    +{images.length - 2}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    </Grid>
                                                )}
                                            </Grid>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Grow>
                {imageQueue.length === 0 && <Grow in={imageQueue.length === 0}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2, mx: 2, alignItems: 'center', background: 'white', boxShadow: 3, borderRadius: 4, p: 4 }}>
                        <CheckCircleOutlineIcon sx={{ width: 40, height: 40, color: '#FFC600' }} />
                        <Typography sx={{ fontSize: 18, fontWeight: 'bold', mt: 2, textAlign: 'center' }}>
                            {t('AllPhotosHaveBeenSynchronized!')}
                        </Typography>
                    </Box>
                </Grow>}
                <Box sx={{ borderBottom: '1px solid lightgray', my: 0, mx: 1, mt: 2 }} />

                {/* <Grow in={categories?.length > 0} > */}

                {/*  <Stack direction='row' spacing={1} justifyContent={'center'} sx={{ mt: 2 }}>
                            <Folder sx={{ my: 1, fontSize: 23 }} />
                            <Typography sx={{ fontSize: 18, fontWeight: 'bold', my: 1, textAlign: 'end' }}>
                                {t('buildings.folders')}
                            </Typography>
                        </Stack> */}
                <Box sx={{
                    flexGrow: 1,
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    pt: 1,
                    pb: 13
                }}>
                    {categories?.map((category, index) => (
                        <Box key={'cat_' + index} sx={{ display: 'flex', flexDirection: 'column', mt: 1, mx: 2, alignItems: 'center', background: 'white', boxShadow: 0, borderRadius: 2, px: 1, border: '1px solid lightgray', marginRight: 6 }}>
                            <React.Fragment key={category.id}>
                                <Box
                                    onClick={() => {
                                        setCategoryId(category.id);
                                        setCameraOpen(true);
                                    }}
                                    sx={{ width: '100%', borderBottom: '1px solid #ccc', cursor: 'pointer' }}
                                    p={1.5} display={'flex'}
                                    justifyContent={'space-between'}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end', gap: 1 }}>
                                        <FolderOutlined sx={{ fontSize: '40px', margin: '-4px', stroke: 'white', strokeWidth: 0.8 }} />
                                        <Typography fontSize={'20px'} sx={{ fontWeight: '500', paddingLeft: 1, marginBottom: '-1px' }}>{category.name ? truncateText(category.name, 23) : '-'}</Typography>
                                    </Box>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            borderRadius: '50%',
                                            backgroundColor: '#FFC600',
                                            width: 58,
                                            height: 58,
                                            margin: '-13px',
                                            marginRight: -6,
                                            padding: 1,
                                            fontSize: '40px',
                                        }}
                                    >
                                        <CameraAlt fontSize="inherit" sx={{ marginTop: 0.5 }} />
                                    </Box>
                                </Box>
                            </React.Fragment>
                        </Box>
                    ))}
                </Box>
                <BottomIconButton
                    frontIcon={<CameraAltIcon />}
                    onClick={() => {
                        setCategoryId(undefined);
                        setCameraOpen(true);
                    }}
                    title="takephotoWithoutAssign"
                />
            </Box>
        </Box>
    );
}
